import React, { useCallback, useEffect, useRef, useState } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, ActivityIndicator, RefreshControl, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { DirectionalIcon } from '../components/DirectionalIcon';
import { colors, spacing, typography } from '../theme';
import { withAlpha } from '../theme';
import { useAppContext } from '../contexts/AppContext';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { ScreenLayout } from '../components/ScreenLayout';
import { Header } from '../components/Header';
import { NotificationItem, Notification } from '../components/NotificationItem';
import { apiService } from '../services/api';
import { MoreOptionsModal } from '../components/MoreOptionsModal';
import { useNotificationActions } from '../hooks/useNotificationActions';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { useLocale } from '../contexts/LocaleContext';
import { useRTLStyles } from '../hooks/useRTLStyles';
import { openNotificationTarget } from '../navigation/notificationRouter';
import { GROUP_ICON, NOTIFICATION_GROUPS, NotificationGroup, typesInGroup } from '../config/notificationVisuals';

const PAGE = 20;

type Section = { key: 'today' | 'yesterday' | 'earlier'; items: Notification[] };

const sectionsOf = (items: Notification[]): Section[] => {
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  const yesterday = start.getTime() - 24 * 60 * 60 * 1000;
  const out: Section[] = [
    { key: 'today', items: [] },
    { key: 'yesterday', items: [] },
    { key: 'earlier', items: [] },
  ];
  for (const n of items) {
    const at = new Date(n.createdAt).getTime();
    out[at >= start.getTime() ? 0 : at >= yesterday ? 1 : 2].items.push(n);
  }
  return out.filter((s) => s.items.length > 0);
};

/**
 * All notifications: All / Unread, filter by area (messages, sales, …),
 * grouped Today · Yesterday · Earlier; mark all read, delete, and a shortcut
 * to Settings → Notifications.
 */
export const NotificationHistoryScreen: React.FC = () => {
  const styles = useThemedStyles(getStyles);
  const { navigateTo, setActiveTab, activeTab } = useAppContext();
  const { t } = useLocale();
  const rtl = useRTLStyles();

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [group, setGroup] = useState<NotificationGroup | null>(null);
  const [showMoreModal, setShowMoreModal] = useState(false);
  const { handleNotificationPress } = useNotificationActions(setNotifications);
  const requestId = useRef(0);

  const load = useCallback(async (mode: 'reset' | 'refresh' | 'more' = 'reset') => {
    const id = ++requestId.current;
    const offset = mode === 'more' ? notifications.length : 0;
    if (mode === 'reset') setLoading(true);
    if (mode === 'refresh') setRefreshing(true);
    if (mode === 'more') setLoadingMore(true);
    try {
      const res = await apiService.getNotifications({
        limit: PAGE,
        offset,
        unreadOnly,
        ...(group ? { types: typesInGroup(group) } : {}),
      });
      if (id !== requestId.current) return;
      const page: Notification[] = (res.data?.notifications as Notification[]) || [];
      setNotifications((prev) => {
        const merged = mode === 'more' ? [...prev, ...page] : page;
        return merged.filter((n, i, all) => all.findIndex((x) => x._id === n._id) === i);
      });
      setUnreadCount(res.data?.unreadCount || 0);
      const total = Number((res.data as any)?.total ?? 0);
      setHasMore(total ? offset + page.length < total : page.length === PAGE);
    } catch {
      if (mode !== 'more' && id === requestId.current) setNotifications([]);
    } finally {
      if (id === requestId.current) {
        setLoading(false);
        setRefreshing(false);
        setLoadingMore(false);
      }
    }
  }, [unreadOnly, group, notifications.length]);

  useEffect(() => {
    setActiveTab('more');
  }, [setActiveTab]);

  useEffect(() => {
    load('reset');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unreadOnly, group]);

  const markAllRead = async () => {
    try {
      await apiService.authenticatedRequest('/api/notifications/read-all', { method: 'PUT' });
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch {
      // stays as is
    }
  };

  const remove = async (notificationId: string) => {
    try {
      await apiService.authenticatedRequest(`/api/notifications/${notificationId}`, { method: 'DELETE' });
      setNotifications((prev) => prev.filter((n) => n._id !== notificationId));
    } catch {
      // stays as is
    }
  };

  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab, { onMore: () => setShowMoreModal(true) });
  const sections = sectionsOf(notifications);

  const chip = (key: string, label: string, active: boolean, onPress: () => void, icon?: string) => (
    <TouchableOpacity
      key={key}
      style={[styles.chip, active && styles.chipActive, { flexDirection: rtl.rowDirection }]}
      onPress={onPress}
      accessibilityRole="button"
      accessibilityState={{ selected: active }}
    >
      {icon && <Ionicons name={icon as any} size={14} color={(active ? colors.primary : colors.text.secondary) as string} />}
      <Text style={[styles.chipText, active && styles.chipTextActive]}>{label}</Text>
    </TouchableOpacity>
  );

  return (
    <ScreenLayout
      header={
        <Header
          showNotifications={false}
          showMessages
          onMessagePress={() => navigateTo('chat')}
          showNavigation={Platform.OS === 'web'}
          activeTab={activeTab}
          onTabPress={handleTabPress}
        />
      }
      showBottomTabs={Platform.OS !== 'web'}
      onLeftPaneClose={() => {}}
      leftPaneContent="contacts"
      rightPaneContent="empty"
    >
      <View style={styles.container}>
        <View style={[styles.header, { flexDirection: rtl.rowDirection }]}>
          <TouchableOpacity style={styles.headerButton} onPress={() => navigateTo('home')} accessibilityLabel={t('common.back')}>
            <DirectionalIcon name="chevron-back" size={22} color={colors.text.primary as string} />
          </TouchableOpacity>
          <View style={styles.titleWrap}>
            <Text style={[styles.title, { textAlign: rtl.textStart }]}>{t('notifications.title')}</Text>
            <Text style={[styles.subtitle, { textAlign: rtl.textStart }]}>
              {unreadCount > 0 ? t('notifications.unreadCount', { count: unreadCount }) : t('notifications.allRead')}
            </Text>
          </View>
          {unreadCount > 0 && (
            <TouchableOpacity style={styles.headerButton} onPress={markAllRead} accessibilityLabel={t('notifications.markAllRead')}>
              <Ionicons name="checkmark-done" size={20} color={colors.primary as string} />
            </TouchableOpacity>
          )}
          <TouchableOpacity
            style={styles.headerButton}
            onPress={() => openNotificationTarget(navigateTo, 'settings:notifications')}
            accessibilityLabel={t('notifications.settings')}
          >
            <Ionicons name="settings-outline" size={20} color={colors.text.primary as string} />
          </TouchableOpacity>
        </View>

        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          style={styles.chipBar}
          contentContainerStyle={[styles.chipRow, { flexDirection: rtl.rowDirection }]}
        >
          {chip('all', t('notifications.filter.all'), !unreadOnly && !group, () => { setUnreadOnly(false); setGroup(null); })}
          {chip('unread', t('notifications.filter.unread'), unreadOnly, () => setUnreadOnly((v) => !v))}
          <View style={styles.chipDivider} />
          {NOTIFICATION_GROUPS.map((g) => chip(g, t(`notifications.group.${g}` as any), group === g, () => setGroup(group === g ? null : g), GROUP_ICON[g]))}
        </ScrollView>

        <ScrollView
          style={styles.list}
          contentContainerStyle={styles.listContent}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={() => load('refresh')} tintColor={colors.primary as string} colors={[colors.primary as string]} />}
          onScroll={({ nativeEvent }) => {
            const { layoutMeasurement, contentOffset, contentSize } = nativeEvent;
            if (contentSize.height - contentOffset.y - layoutMeasurement.height < 120 && hasMore && !loadingMore && !loading) load('more');
          }}
          scrollEventThrottle={200}
        >
          {loading ? (
            <View style={styles.center}><ActivityIndicator size="large" color={colors.primary as string} /></View>
          ) : notifications.length === 0 ? (
            <View style={styles.empty}>
              <View style={styles.emptyIcon}>
                <Ionicons name={unreadOnly ? 'checkmark-done' : 'notifications-outline'} size={30} color={colors.primary as string} />
              </View>
              <Text style={styles.emptyTitle}>{t(unreadOnly ? 'notifications.caughtUp' : 'notifications.empty')}</Text>
              <Text style={styles.emptyText}>{t('notifications.emptyHint')}</Text>
            </View>
          ) : (
            <>
              {sections.map((section) => (
                <View key={section.key}>
                  <Text style={[styles.sectionTitle, { textAlign: rtl.textStart }]}>{t(`notifications.section.${section.key}` as any)}</Text>
                  <View style={styles.card}>
                    {section.items.map((n) => (
                      <NotificationItem key={n._id} notification={n} onPress={handleNotificationPress} onDelete={remove} />
                    ))}
                  </View>
                </View>
              ))}
              {loadingMore && <ActivityIndicator style={styles.more} color={colors.primary as string} />}
              {Platform.OS === 'web' && hasMore && !loadingMore && (
                <TouchableOpacity style={styles.moreButton} onPress={() => load('more')}>
                  <Text style={styles.moreText}>{t('notifications.loadMore')}</Text>
                </TouchableOpacity>
              )}
            </>
          )}
        </ScrollView>
      </View>
      <MoreOptionsModal visible={showMoreModal} onClose={() => setShowMoreModal(false)} />
    </ScreenLayout>
  );
};

const getStyles = () => StyleSheet.create({
  container: { flex: 1, backgroundColor: colors.background.primary },
  header: { alignItems: 'center', gap: spacing.sm, paddingHorizontal: spacing.md, paddingVertical: spacing.sm + 2 },
  headerButton: { width: 36, height: 36, borderRadius: 18, alignItems: 'center', justifyContent: 'center', borderWidth: 1, borderColor: colors.border },
  titleWrap: { flex: 1, minWidth: 0 },
  title: { fontSize: typography.fontSize.lg, fontFamily: typography.fontFamily.bold, color: colors.text.primary },
  subtitle: { fontSize: typography.fontSize.xs, color: colors.text.secondary, marginTop: 1 },
  chipBar: { flexGrow: 0 },
  chipRow: { gap: spacing.xs, paddingHorizontal: spacing.md, paddingBottom: spacing.sm, alignItems: 'center' },
  chip: {
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: spacing.md,
    paddingVertical: 7,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.background.surface,
  },
  chipActive: { borderColor: colors.primary, backgroundColor: withAlpha(colors.primary as string, 0.08) },
  chipText: { fontSize: typography.fontSize.sm, color: colors.text.secondary },
  chipTextActive: { color: colors.primary, fontFamily: typography.fontFamily.semibold },
  chipDivider: { width: 1, height: 20, backgroundColor: colors.border, marginHorizontal: 2 },
  list: { flex: 1 },
  listContent: { paddingHorizontal: spacing.md, paddingBottom: spacing.xl * 2, maxWidth: 760, width: '100%', alignSelf: 'center' },
  sectionTitle: {
    fontSize: typography.fontSize.xs,
    fontFamily: typography.fontFamily.semibold,
    color: colors.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    marginTop: spacing.md,
    marginBottom: spacing.xs,
  },
  card: { borderRadius: 14, borderWidth: 1, borderColor: colors.border, overflow: 'hidden', backgroundColor: colors.background.surface },
  center: { paddingVertical: spacing.xl * 2, alignItems: 'center' },
  empty: { alignItems: 'center', paddingVertical: spacing.xl * 2, gap: spacing.sm },
  emptyIcon: { width: 64, height: 64, borderRadius: 32, alignItems: 'center', justifyContent: 'center', backgroundColor: withAlpha(colors.primary as string, 0.1) },
  emptyTitle: { fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.semibold, color: colors.text.primary },
  emptyText: { fontSize: typography.fontSize.sm, color: colors.text.secondary, textAlign: 'center', maxWidth: 320 },
  more: { marginVertical: spacing.md },
  moreButton: { alignSelf: 'center', marginTop: spacing.md, paddingHorizontal: spacing.lg, paddingVertical: spacing.sm, borderRadius: 18, borderWidth: 1, borderColor: colors.border },
  moreText: { fontSize: typography.fontSize.sm, color: colors.primary, fontFamily: typography.fontFamily.semibold },
});
